import { useUserStore } from '@/stores/userStore';
import { InteractionRequiredAuthError, LogLevel, PublicClientApplication, type AccountInfo } from '@azure/msal-browser'
import config from '@/services/ConfigService'

class AuthService {
  private msalInstance: PublicClientApplication | null = null
  private isInitialized: boolean = false

  private getMsalInstance(): PublicClientApplication {
    if (!this.msalInstance) {
      this.msalInstance = new PublicClientApplication({
        auth: {

          clientId: config.msalClientId,
          authority: 'https://login.microsoftonline.com/' + config.msalTenantId,
          redirectUri: window.location.origin + '/auth/callback',
          postLogoutRedirectUri: window.location.origin,
          navigateToLoginRequestUrl: false
        },
        cache: {
          cacheLocation: 'localStorage'
        },
        system: {
          loggerOptions: {
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
              if (containsPii) {
                return;
              }
              switch (level) {
                case LogLevel.Error:
                  console.error(message);
                  return;
                case LogLevel.Info:
                  console.info(message);
                  return;
                case LogLevel.Verbose:
                  console.debug(message);
                  return;
                case LogLevel.Warning:
                  console.warn(message);
                  return;
                default:
                  return;
              }
            },
            logLevel: LogLevel.Warning
          }
        }
      })
    }
    return this.msalInstance
  }

  async initialize(): Promise<void> {
    console.debug('[AuthService]: Initializing')
    await this.getMsalInstance().initialize()
    this.isInitialized = true
    console.debug('[AuthService]: Initialize completed')
  }

  async handleRedirectPromise(): Promise<boolean> {
    try {
      const userStore = useUserStore()
      const response = await this.getMsalInstance().handleRedirectPromise()
      if (response) {
        userStore.azureAccount = response.account
        return true
      }

      // Check if user is already logged in
      const accounts = this.getMsalInstance().getAllAccounts()
      if (accounts.length === 0) {
        const hash = window.location.hash
        if (hash.includes('error=')) {
          const params = new URLSearchParams(hash.substring(1));
          console.error("Auth Error:", params.get("error"));
          console.error("Error Description:", params.get("error_description"));
          return false
        }
      }
      if (accounts.length > 0) {
        userStore.azureAccount = accounts[0]
        return true
      }

      else return false
    } catch (error) {
      console.error('Error handling redirect:', error)
      return false
    }
  }

  async loginRedirect(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize()
    }

    try {
      await this.getMsalInstance().loginRedirect({
        scopes: ['openid', config.msalScope],
      })
    }
    catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        this.reset()
        return this.loginRedirect()
      }
    }
  }

  async getAccessToken(): Promise<string | void> {
    if (!this.isInitialized) {
      await this.initialize()
    }

    const request = {
      scopes: ['openid', config.msalScope]
    }

    try {
      const accounts = this.getMsalInstance().getAllAccounts()
      if (accounts.length === 0) throw new Error('No authenticated user')
  
      const response = await this.getMsalInstance().acquireTokenSilent({ ...request, account: accounts[0] })
      return response.accessToken
    }
    catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return await this.getMsalInstance().acquireTokenRedirect(request)
      }
    }
  }

  getAllAccounts(): AccountInfo[] {
    return this.getMsalInstance().getAllAccounts()
  }

  logout(): void {
    const account = this.getMsalInstance().getActiveAccount()
    this.getMsalInstance().logoutRedirect({ account })
  }

  reset() {
    sessionStorage.clear()
  }
}

export const authService = new AuthService()